import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  ANNOUNCEMENT_FORM_STATE_OPTIONS,
  ANNOUNCEMENT_STATE,
  mergeAnnouncementDetailsWithLanguages,
  stripAnnouncementDetails,
} from './announcementCenterConstants';

const EMPTY_FORM = {
  sort: 1,
  state: ANNOUNCEMENT_STATE.ON,
  imgUrl: '',
  filmUrl: '',
  details: [],
};

export default function AnnouncementCenterEditModal({ open, announcementId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(announcementId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (announcementId) {
          const data = await adminApi.getAnnouncementCenter(announcementId);
          if (cancelled) {
            return;
          }
          setForm({
            sort: data.sort ?? 1,
            state: data.state ?? ANNOUNCEMENT_STATE.ON,
            imgUrl: data.imgUrl || '',
            filmUrl: data.filmUrl || '',
            details: mergeAnnouncementDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeAnnouncementDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, announcementId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  function handleReset() {
    setError('');
    if (isEdit) {
      setForm((prev) => ({
        ...prev,
        details: prev.details.map((detail) => ({ ...detail, title: '', body: '' })),
      }));
      return;
    }
    setForm({
      ...EMPTY_FORM,
      details: mergeAnnouncementDetailsWithLanguages(languages, []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripAnnouncementDetails(form.details).filter((d) => d.title?.trim());
    if (!details.length) {
      setError('至少填寫一個語系的標題');
      return;
    }
    const payload = {
      state: Number(form.state),
      sort: Number(form.sort),
      imgUrl: form.imgUrl?.trim() || '',
      filmUrl: form.filmUrl?.trim() || '',
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateAnnouncementCenter({ id: announcementId, ...payload });
      } else {
        await adminApi.createAnnouncementCenter(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body announcement-center-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <label htmlFor="announcementSort">
                <span className="form-label-required">顯示排序</span>
              </label>
              <input
                id="announcementSort"
                type="number"
                min="0"
                value={form.sort}
                onChange={(e) => setForm((p) => ({ ...p, sort: e.target.value }))}
                required
              />
            </div>

            <div className="form-field">
              <span className="form-field__label">狀態</span>
              <div className="product-status-radios">
                {ANNOUNCEMENT_FORM_STATE_OPTIONS.map((option) => (
                  <label key={option.value} className="product-status-radios__item">
                    <input
                      type="radio"
                      name="announcementState"
                      checked={Number(form.state) === option.value}
                      onChange={() => setForm((p) => ({ ...p, state: option.value }))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="announcementImgUrl">圖片連結</label>
              <input
                id="announcementImgUrl"
                type="text"
                value={form.imgUrl}
                onChange={(e) => setForm((p) => ({ ...p, imgUrl: e.target.value }))}
                placeholder="請輸入圖片連結"
              />
            </div>

            <div className="form-field">
              <label htmlFor="announcementFilmUrl">影片連結</label>
              <input
                id="announcementFilmUrl"
                type="text"
                value={form.filmUrl}
                onChange={(e) => setForm((p) => ({ ...p, filmUrl: e.target.value }))}
                placeholder="請輸入影片連結（選填）"
              />
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="announcement-center-lang-section">
                <div className="form-field">
                  <label htmlFor={`announcement-title-${detail.lang}`}>
                    <span className="form-label-required">標題（{detail._label || detail.lang}）</span>
                  </label>
                  <input
                    id={`announcement-title-${detail.lang}`}
                    type="text"
                    value={detail.title || ''}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                  />
                </div>

                <div className="form-field form-field--full">
                  <span className="form-label">內容（{detail._label || detail.lang}）</span>
                  <RichTextEditor
                    value={detail.body || ''}
                    onChange={(value) => updateDetail(index, 'body', value)}
                  />
                </div>
              </section>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
                重置
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
